/**
 * Ported verbatim from student-dashboard-v2/src/hooks/useMediaLoader.ts.
 * Loads signed URLs for S3 media on-demand, with the exact same decision logic
 * as the student website:
 *   - already-signed URLs (X-Amz-/Signature=) are returned as-is
 *   - http(s) URLs are returned as-is
 *   - bare S3 keys are signed via the media service
 */
import {useCallback, useRef, useState} from 'react';
import {getFileUrlByKey, getFileUrlsByKeys} from './getS3Url';

export const useMediaLoader = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingUrls, setLoadingUrls] = useState<Set<string>>(new Set());
  const urlCache = useRef<Map<string, string>>(new Map());

  const loadMediaUrl = useCallback(
    async (url: string): Promise<string | null> => {
      if (!url || url === 'undefined') return null;

      if (urlCache.current.has(url)) {
        return urlCache.current.get(url)!;
      }

      if (url.includes('X-Amz-') || url.includes('Signature=')) {
        return url;
      }

      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }

      setLoadingUrls(prev => new Set(prev).add(url));
      setIsLoading(true);

      try {
        const signedUrl = await getFileUrlByKey(url);
        if (signedUrl) {
          urlCache.current.set(url, signedUrl);
          return signedUrl;
        }
        return null;
      } catch (error) {
        console.error('Failed to load media URL:', error);
        return null;
      } finally {
        setLoadingUrls(prev => {
          const newSet = new Set(prev);
          newSet.delete(url);
          setIsLoading(newSet.size > 0);
          return newSet;
        });
      }
    },
    [],
  );

  const loadMultipleMediaUrls = useCallback(
    async (urls: string[]): Promise<(string | null)[]> => {
      const results = new Array<string | null>(urls.length).fill(null);
      const toFetch: Array<{url: string; index: number}> = [];

      urls.forEach((url, index) => {
        if (!url || url === 'undefined') return;

        if (urlCache.current.has(url)) {
          results[index] = urlCache.current.get(url)!;
          return;
        }

        if (
          url.includes('X-Amz-') ||
          url.includes('Signature=') ||
          url.startsWith('http://') ||
          url.startsWith('https://')
        ) {
          results[index] = url;
          return;
        }

        toFetch.push({url, index});
      });

      if (!toFetch.length) return results;

      setIsLoading(true);
      setLoadingUrls(prev => {
        const next = new Set(prev);
        toFetch.forEach(({url}) => next.add(url));
        return next;
      });

      try {
        const signedUrls = await getFileUrlsByKeys(
          toFetch.map(({url}) => ({path: url})),
        );

        toFetch.forEach(({url, index}) => {
          const signedUrl = signedUrls[url];
          if (signedUrl) {
            urlCache.current.set(url, signedUrl);
            results[index] = signedUrl;
          }
        });

        return results;
      } catch (error) {
        console.error('Failed to load media URLs:', error);
        return results;
      } finally {
        setLoadingUrls(prev => {
          const next = new Set(prev);
          toFetch.forEach(({url}) => next.delete(url));
          setIsLoading(next.size > 0);
          return next;
        });
      }
    },
    [],
  );

  const clearCache = useCallback(() => {
    urlCache.current.clear();
  }, []);

  const isUrlLoading = useCallback(
    (url: string): boolean => loadingUrls.has(url),
    [loadingUrls],
  );

  return {
    loadMediaUrl,
    loadMultipleMediaUrls,
    isLoading,
    isUrlLoading,
    clearCache,
  };
};
